import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import HeroSection from "../components/HeroSection";
import CategoryGrid from "../components/CategoryGrid";
import AssetCard from "../components/AssetCard";
import Logo from "../components/Logo";
import { useLang } from "../contexts/LangContext";
import { api } from "../lib/api";
import { ArrowUpRight, Download, Database, Users, Clock, Gift } from "lucide-react";

const TABS = ["all", "powerpoint", "word", "excel", "images", "3d-icons", "icons", "animation"];

export default function Home() {
  const { t } = useLang();
  const [tab, setTab] = useState("all");
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total_files: 0, users: 0, downloads: 0 });

  useEffect(() => {
    api
      .get("/files", { params: { sort: "popular", limit: 8, category: tab === "all" ? undefined : tab } })
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]));
  }, [tab]);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const tabLabel = (id) => {
    const map = {
      all: t("allAssets"), powerpoint: t("powerpoint"), word: t("word"), excel: t("excel"),
      images: t("images"), "3d-icons": t("threeDIcons"), icons: t("icons"), animation: t("animation"),
    };
    return map[id] || id;
  };

  return (
    <main>
      <HeroSection />
      <CategoryGrid />

      {/* Most Downloaded — editorial */}
      <section className="border-t py-20 sm:py-28" style={{ borderColor: "hsl(var(--border))" }} data-testid="most-downloaded-section">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <div className="grid grid-cols-1 items-end gap-8 lg:grid-cols-2">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-fg">§03 — This week</div>
              <h2 className="font-display mt-4 text-5xl leading-[0.95] sm:text-6xl">
                <span className="font-serif text-accent">Most</span>{" "}
                <span className="text-ink">downloaded.</span>
              </h2>
            </div>
            <p className="max-w-md text-base text-muted-fg lg:text-end">{t("mostDownloadedDesc")}</p>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-2 overflow-x-auto pb-2">
            {TABS.map((id) => (
              <button
                key={id}
                onClick={() => setTab(id)}
                className={`shrink-0 rounded-full border px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] transition-colors ${
                  tab === id
                    ? "border-[hsl(var(--ink))] bg-[hsl(var(--ink))] text-[hsl(var(--bg))]"
                    : "text-[hsl(var(--ink-2))] hover:bg-[hsl(var(--surface-2))]"
                }`}
                style={{ borderColor: tab === id ? "hsl(var(--ink))" : "hsl(var(--border))" }}
                data-testid={`home-tab-${id}`}
              >
                {tabLabel(id)}
              </button>
            ))}
          </div>

          {items.length === 0 ? (
            <div className="mt-12 card flex flex-col items-center justify-center p-16 text-center">
              <div className="font-display text-3xl text-muted-fg">{t("noResults")}</div>
              <Link to="/upload" className="btn btn-accent mt-6">
                {t("upload")} <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          ) : (
            <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {items.map((it, idx) => (
                <AssetCard key={it.id} item={it} rank={idx + 1} />
              ))}
            </div>
          )}

          <div className="mt-10 flex justify-center">
            <Link to="/browse" className="btn btn-ghost">
              {t("browseCollections")}
              <ArrowUpRight className="h-4 w-4 rtl:rotate-90" />
            </Link>
          </div>
        </div>
      </section>

      {/* Stats band */}
      <section className="border-t py-20 sm:py-28" style={{ borderColor: "hsl(var(--border))" }} data-testid="quality-section">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <div className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-fg">§04 — Numbers</div>
              <h2 className="font-display mt-4 text-5xl leading-[0.95] sm:text-6xl">
                <span className="text-ink">A library</span>
                <br />
                <span className="font-serif text-accent">that grows</span>
                <br />
                <span className="text-ink">every day.</span>
              </h2>
              <p className="mt-6 max-w-md text-base text-muted-fg">{t("qualityDesc")}</p>
            </div>

            <div className="lg:col-span-7">
              <div className="grid grid-cols-2 gap-4">
                <StatTile icon={Database} value={stats.total_files} label={t("freeAssets")} big />
                <StatTile icon={Users} value={stats.users} label={t("happyCreators")} />
                <StatTile icon={Download} value={stats.downloads} label={t("downloads")} />
                <StatTile icon={Clock} value="24/7" label={t("freeAccess")} isText />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 sm:py-28" data-testid="cta-section">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <div
            className="grain relative overflow-hidden rounded-[2rem] p-10 sm:p-16"
            style={{
              backgroundColor: "hsl(var(--ink))",
              color: "hsl(var(--bg))",
            }}
          >
            <Logo
              size={520}
              color="hsl(var(--bg) / 0.05)"
              className="pointer-events-none absolute -end-20 -top-20"
            />
            <div className="relative z-10 grid grid-cols-1 items-end gap-8 lg:grid-cols-2">
              <div>
                <span className="chip" style={{ background: "transparent", borderColor: "hsl(var(--bg) / 0.3)", color: "hsl(var(--bg))" }}>
                  <Gift className="h-3 w-3" />
                  {t("free100")}
                </span>
                <h3 className="font-display mt-6 text-5xl leading-[0.95] sm:text-7xl">
                  {t("readyToDownload").replace("?", "")}
                  <span className="font-serif text-accent">?</span>
                </h3>
                <p className="mt-6 max-w-md text-base opacity-80">{t("readyToDownloadDesc")}</p>
              </div>
              <div className="flex flex-col gap-3 lg:items-end">
                <Link
                  to="/browse"
                  className="btn !text-base !px-7 !py-4"
                  style={{ background: "hsl(var(--accent))", color: "hsl(var(--accent-fg))" }}
                  data-testid="cta-start-downloading"
                >
                  <Download className="h-4 w-4" />
                  {t("startDownloading")}
                </Link>
                <Link
                  to="/upload"
                  className="btn !px-6 !py-3"
                  style={{
                    border: "1px solid hsl(var(--bg) / 0.3)",
                    color: "hsl(var(--bg))",
                  }}
                  data-testid="cta-upload"
                >
                  {t("upload")} <ArrowUpRight className="h-4 w-4" />
                </Link>
                <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-[11px] uppercase tracking-[0.18em] opacity-70 lg:justify-end">
                  <span>✓ {t("noCard")}</span>
                  <span>✓ {t("noSubs")}</span>
                  <span>✓ {t("noFees")}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

function StatTile({ icon: Icon, value, label, isText, big }) {
  return (
    <div
      className={`card flex flex-col justify-between p-6 ${big ? "row-span-2" : ""}`}
      style={{ minHeight: big ? 280 : 130 }}
    >
      <Icon className="h-5 w-5 text-muted-fg" />
      <div>
        <div className={`font-display ${big ? "text-7xl" : "text-4xl"} text-ink leading-none`}>
          {isText ? value : <>{value}<span className="text-accent">+</span></>}
        </div>
        <div className="mt-2 text-[11px] uppercase tracking-[0.18em] text-muted-fg">{label}</div>
      </div>
    </div>
  );
}
